import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'

import * as enrollmentActions from '../../../actions/enrollmentActions'
import { reset as resetDualPanelData } from '../../../reducers/dualPanelSelectorReducer'

import { Tab, Tabs, TabList, TabPanel } from '../../common/Tabs'

import ProfileAvatar from '../../common/avatar/ProfileAvatar'
import Loader from '../../common/loader/Loader'
import ApproveEnrollmentModal from '../../classes/program-dashboard-manager/enrollments-tab/modals/ApproveEnrollmentModal'
import DropEnrollmentModal from '../../classes/program-dashboard-manager/enrollments-tab/modals/DropEnrollmentModal'
import RefundEnrollmentModal from '../../classes/program-dashboard-manager/enrollments-tab/modals/RefundEnrollmentModal'
import WaitlistProgramSelectorModal from '../../classes/program-dashboard-manager/enrollments-tab/waitlist/modals/WaitlistProgramSelectorModal'
import SuccessModal from '../../common/customModal/SuccessModal'
import TransferConfirmationModal from './TransferConfirmationModal'
import DeleteEnrollmentModal from './DeleteEnrollmentModal'

import { ReactComponent as CloseIcon } from '../../../assets/images/common/icons/modal-X-icon.svg'
import { ReactComponent as RefundIcon } from '../../../assets/images/common/icons/history-icon.svg'
import { ReactComponent as TransferIcon } from '../../../assets/images/common/icons/transfer-icon.svg'
import { ReactComponent as PlusIcon } from '../../../assets/images/common/icons/circle-plus-icon.svg'
import { ReactComponent as DeleteIcon } from '../../../assets/images/common/icons/delete-icon.svg'
import { ReactComponent as WarningIcon } from '../../../assets/images/common/icons/warning-icon.svg'
import { ReactComponent as PersonIcon } from '../../../assets/images/common/icons/person-icon.svg'
import { ReactComponent as FamilyIcon } from '../../../assets/images/common/icons/family-icon.svg'
import { ReactComponent as CallIcon } from '../../../assets/images/common/icons/call-icon.svg'
import { ReactComponent as MailIcon } from '../../../assets/images/common/icons/mail-icon.svg'
import { ReactComponent as CopyIcon } from '../../../assets/images/common/icons/copy-icon.svg'
import { ReactComponent as CalendarIcon } from '../../../assets/images/common/icons/calendar-icon.svg'
import { ReactComponent as GolferIcon } from '../../../assets/images/common/icons/golfer-half-icon.svg'

import { requestGetEnrollment } from '../../../actions/enrollmentsBillingActions'
import { deserialize } from '../../../utility/serialization'
import { calculateAge, genderLabel } from '../../../utility/userHelper'
import { formatPhoneForLinks } from '../../../utility/phoneHelper'
import {
  ENROLLMENT_STATUS_LABELS,
  ACTIVITY_LABELS,
  formatInvoiceStatusDisplay,
  formatPaymentMethodDisplay,
  hasBillingTab,
} from '../../../utility/enrollmentsBilling'
import { displayWithCurrency } from '../../../utility/currency'

import classes from './EnrollmentDetailDrawer.module.scss'
import colors from '../../../assets/styles/globals.scss'


const EnrollmentDetailDrawer = ({ enrollment: listEnrollment, communityId, onClose, onActionComplete }) => {
  const dispatch = useDispatch()
  const [enrollment, setEnrollment] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const [activeModal, setActiveModal] = useState(null) // 'approve' | 'drop' | 'remove' | null
  const [actionLoading, setActionLoading] = useState(false)
  const [coachMessage, setCoachMessage] = useState('')
  const [notifyViaEmail, setNotifyViaEmail] = useState(true)

  // Transfer flow (reuses the waitlist program selector)
  const [showTransfer, setShowTransfer] = useState(false)
  const [showTransferConfirm, setShowTransferConfirm] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [showTransferSuccess, setShowTransferSuccess] = useState(false)

  const loadEnrollment = useCallback(() => {
    setIsLoading(true)
    return requestGetEnrollment({ communityId, enrollmentId: listEnrollment.id })
      .then(response => {
        const data = deserialize(response.data)
        setEnrollment(data.enrollment)
      })
      .catch(() => {
        toast.error('Failed to load enrollment details', { position: toast.POSITION.TOP_RIGHT })
      })
      .finally(() => setIsLoading(false))
  }, [communityId, listEnrollment.id])

  useEffect(() => {
    loadEnrollment()
  }, [loadEnrollment])

  const closeModal = () => {
    setActiveModal(null)
    setCoachMessage('')
    setNotifyViaEmail(true)
  }

  const handleApprove = () => {
    setActionLoading(true)
    dispatch(enrollmentActions.updateEnrollment({ enrollmentData: { id: listEnrollment.id, status: 'active' }, coachMessage })).unwrap()
      .then(() => {
        toast.success('Enrollment approved successfully.', { position: toast.POSITION.TOP_RIGHT })
        closeModal()
        onActionComplete?.()
        loadEnrollment()
      })
      .catch(() => toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setActionLoading(false))
  }

  // "Delete" (and the pending "Decline") soft-deletes the enrollment via the deleteEnrollment thunk:
  // DELETE /api/v1/enrollments/:id. The record is discarded and leaves the list. For online-paid
  // enrollments the refund flag is passed through (RefundEnrollmentModal), mirroring the roster drop.
  const handleRemoveEnrollment = (refund = false) => {
    const wasActive = (enrollment || listEnrollment)?.status === 'active'
    setActionLoading(true)
    dispatch(enrollmentActions.deleteEnrollment({ enrollmentId: listEnrollment.id, refund, coachMessage, notifyViaEmail })).unwrap()
      .then(() => {
        toast.success(`Enrollment ${wasActive ? 'deleted' : 'declined'} successfully.`, { position: toast.POSITION.TOP_RIGHT })
        closeModal()
        onActionComplete?.()
        onClose()
      })
      .catch(() => toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setActionLoading(false))
  }

  const handleDrop = () => {
    setActionLoading(true)
    dispatch(enrollmentActions.updateEnrollment({
      enrollmentData: { id: listEnrollment.id, status: 'dropped' }, coachMessage, notifyViaEmail,
    })).unwrap()
      .then(() => {
        toast.success('Enrollment dropped successfully.', { position: toast.POSITION.TOP_RIGHT })
        closeModal()
        onActionComplete?.()
        loadEnrollment()
      })
      .catch(() => toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setActionLoading(false))
  }

  const closeTransfer = () => {
    setShowTransfer(false)
    setShowTransferConfirm(false)
    setSelectedCourse(null)
    dispatch(resetDualPanelData())
  }

  const handleTransfer = ({ coachMessage: message, sendEmail }) => {
    if (!selectedCourse) { return }

    setActionLoading(true)
    dispatch(enrollmentActions.transferEnrollment({
      enrollmentId: listEnrollment.id, courseId: selectedCourse.id, coachMessage: message, sendEmail,
    })).unwrap()
      .then(() => {
        closeTransfer()
        onActionComplete?.()
        loadEnrollment()
        setShowTransferSuccess(true)
      })
      .catch(() => toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setActionLoading(false))
  }

  const student = enrollment?.student || listEnrollment.student
  const displayStatus = enrollment?.displayStatus || listEnrollment.displayStatus
  const showBillingTab = enrollment ? hasBillingTab(enrollment) : false
  const invoice = enrollment?.invoice
  const isOutstanding = invoice?.status === 'outstanding'
  const paidOnline = enrollment?.paymentStatus === 'paid_online'

  // RefundEnrollmentModal expects the program-roster enrollment shape (user + snake-case paid_at);
  // map our detail payload onto it so the transaction breakdown renders correctly.
  const refundModalEnrollment = enrollment && { ...enrollment, user: enrollment.student, paid_at: enrollment.paidAt }

  return (
    <>
      <div className={classes.drawer}>
        <header className={classes.header}>
          <button className={classes.closeButton} onClick={onClose}>
            <CloseIcon className={classes.closeIcon} aria-hidden='true' />
          </button>
        </header>

        {isOutstanding && invoice?.lastReminderSentAt && (
          <OutstandingBanner lastReminderDate={invoice.lastReminderSentAt} />
        )}

        <Tabs className={classes.tabs}>
          <TabList className={classes.tabList}>
            <Tab className={classes.tab}>Enrollments</Tab>
            {showBillingTab && <Tab className={classes.tab}>Billing</Tab>}
            <Tab className={classes.tab}>Contact Info</Tab>
          </TabList>

          <div className={classes.studentHeader}>
            <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
            <div className={classes.studentInfo}>
              <h6 className='mb-0 font-weight-bold'>{student.firstName} {student.lastName}</h6>
              <span className={classes.studentMeta}>{student.email}</span>
            </div>
            {displayStatus === 'pending_approval' && (
              <div className={classes.inlineActions}>
                <button className={classes.approveButton} onClick={() => setActiveModal('approve')} disabled={actionLoading}>
                  Approve
                </button>
                <button className={classes.declineButton} onClick={() => setActiveModal('remove')} disabled={actionLoading}>
                  Decline
                </button>
              </div>
            )}
          </div>

          <TabPanel>
            {isLoading || !enrollment ? (
              <div className={classes.loaderWrapper}>
                {isLoading ? <Loader>Loading details...</Loader> : <span>Failed to load details.</span>}
              </div>
            ) : (
              <EnrollmentTab
                enrollment={enrollment}
                displayStatus={displayStatus}
                onDrop={() => setActiveModal(paidOnline ? 'dropRefund' : 'drop')}
                onRemove={() => setActiveModal('delete')}
                onTransfer={() => setShowTransfer(true)}
              />
            )}
          </TabPanel>
          {showBillingTab && (
            <TabPanel>
              <BillingTab enrollment={enrollment} />
            </TabPanel>
          )}
          <TabPanel>
            {enrollment ? <ContactTab enrollment={enrollment} /> : null}
          </TabPanel>
        </Tabs>
      </div>
      <div
        tabIndex={0}
        role='button'
        onKeyDown={() => null}
        className={classes.drawerOverlay}
        onClick={onClose}
      />

      <EnrollmentActionModals
        activeModal={activeModal}
        closeModal={closeModal}
        coachMessage={coachMessage}
        setCoachMessage={setCoachMessage}
        notifyViaEmail={notifyViaEmail}
        toggleNotify={() => setNotifyViaEmail(notify => !notify)}
        isLoading={actionLoading}
        onApprove={handleApprove}
        onDrop={handleDrop}
        onRemove={() => handleRemoveEnrollment(false)}
        onRefund={handleRemoveEnrollment}
        dropEnrollment={enrollment || listEnrollment}
        refundEnrollment={refundModalEnrollment}
      />

      {showTransfer && enrollment?.course && (
        <WaitlistProgramSelectorModal
          isOpen={showTransfer}
          title='Transfer to Another Program'
          currentProgram={enrollment.course}
          selectedProgram={selectedCourse}
          onSelectProgram={setSelectedCourse}
          students={enrollment.student ? [enrollment.student] : []}
          onClose={closeTransfer}
          onProceed={() => { setShowTransfer(false); setShowTransferConfirm(true) }}
        />
      )}

      {showTransferConfirm && enrollment && (
        <TransferConfirmationModal
          isOpen
          enrollment={enrollment}
          onClose={closeTransfer}
          onConfirm={handleTransfer}
          isLoading={actionLoading}
        />
      )}

      <SuccessModal
        isOpen={showTransferSuccess}
        onClose={() => setShowTransferSuccess(false)}
        title='Enrollment Transferred Successfully'
        buttonMessage='Close'
      />
    </>
  )
}

// --- Action Modals (approve / drop / delete / refund) ---

const EnrollmentActionModals = ({
  activeModal, closeModal, coachMessage, setCoachMessage, notifyViaEmail, toggleNotify,
  isLoading, onApprove, onDrop, onRemove, onRefund, dropEnrollment, refundEnrollment,
}) => (
  <>
    {activeModal === 'approve' && (
      <ApproveEnrollmentModal
        showModal
        closeModal={closeModal}
        coachMessage={coachMessage}
        handleCoachMessageChange={e => setCoachMessage(e.target.value)}
        handleApproveEnrollment={onApprove}
        isLoading={isLoading}
      />
    )}

    {(activeModal === 'drop' || activeModal === 'remove') && (
      <DropEnrollmentModal
        showModal
        closeModal={closeModal}
        coachMessage={coachMessage}
        handleCoachMessageChange={e => setCoachMessage(e.target.value)}
        handleRemoveEnrollment={activeModal === 'drop' ? onDrop : onRemove}
        enrollment={dropEnrollment}
        notifyViaEmail={notifyViaEmail}
        handleCheckboxChange={toggleNotify}
        isLoading={isLoading}
      />
    )}

    {activeModal === 'refund' && refundEnrollment && (
      <RefundEnrollmentModal
        showModal
        closeModal={closeModal}
        coachMessage={coachMessage}
        handleCoachMessageChange={e => setCoachMessage(e.target.value)}
        handleRemoveEnrollment={onRefund}
        enrollment={refundEnrollment}
        notifyViaEmail={notifyViaEmail}
        handleCheckboxChange={toggleNotify}
        isLoading={isLoading}
      />
    )}

    {/* Dropping a paid-online enrollment: confirm via the refund modal; the drop refunds server-side. */}
    {activeModal === 'dropRefund' && refundEnrollment && (
      <RefundEnrollmentModal
        showModal
        closeModal={closeModal}
        coachMessage={coachMessage}
        handleCoachMessageChange={e => setCoachMessage(e.target.value)}
        handleRemoveEnrollment={onDrop}
        enrollment={refundEnrollment}
        notifyViaEmail={notifyViaEmail}
        handleCheckboxChange={toggleNotify}
        isLoading={isLoading}
      />
    )}

    {activeModal === 'delete' && (
      <DeleteEnrollmentModal isOpen onClose={closeModal} onConfirm={onRemove} isLoading={isLoading} />
    )}
  </>
)

EnrollmentActionModals.propTypes = {
  activeModal: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  coachMessage: PropTypes.string,
  setCoachMessage: PropTypes.func.isRequired,
  notifyViaEmail: PropTypes.bool,
  toggleNotify: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRefund: PropTypes.func.isRequired,
  dropEnrollment: PropTypes.object,
  refundEnrollment: PropTypes.object,
}

// --- Outstanding Payment Banner ---

const OutstandingBanner = ({ lastReminderDate }) => (
  <div className={classes.outstandingBanner}>
    <WarningIcon className={classes.bannerIcon} />
    <span>Outstanding Payment - Last Reminder Sent on {moment(lastReminderDate).format('M/DD/YYYY')}</span>
  </div>
)

OutstandingBanner.propTypes = {
  lastReminderDate: PropTypes.string.isRequired,
}

// --- Enrollment Action Buttons ---

const EnrollmentActionButtons = ({ displayStatus, invoice, paidOnline, onDrop, onRemove, onTransfer }) => {
  const isPending = displayStatus === 'pending_approval'
  const isWaitlisted = displayStatus === 'waitlisted'
  const isDropped = displayStatus === 'dropped'
  const isCompleted = displayStatus === 'completed'

  if (isPending) { return null }

  const isPaid = invoice?.status === 'paid'
  const isRefunded = invoice?.status === 'refunded' || invoice?.status === 'partially_refunded'
  const showRefundLabel = isPaid || isRefunded

  if (isWaitlisted) {
    return (
      <div className={classes.actionButtons}>
        <button className={classes.actionButton} disabled>
          <PlusIcon className={classes.actionIcon} /> Add to Program
        </button>
        <button className={classes.actionButton} disabled>
          Send Invite Link
        </button>
        <button className={classNames(classes.actionButton, classes.pushRight)} disabled>
          <DeleteIcon className={classes.actionIcon} /> Remove
        </button>
      </div>
    )
  }

  // Completed or dropped enrollments can only be deleted, and only when not paid online.
  if (isDropped || isCompleted) {
    if (paidOnline) { return null }

    return (
      <div className={classes.actionButtons}>
        <button className={classes.actionButton} onClick={onRemove}>
          <DeleteIcon className={classes.actionIcon} /> Delete
        </button>
      </div>
    )
  }

  return (
    <div className={classes.actionButtons}>
      <button className={classes.actionButton} onClick={onDrop}>
        <RefundIcon className={classes.actionIcon} /> {showRefundLabel ? 'Drop/Refund' : 'Drop'}
      </button>
      <button className={classes.actionButton} onClick={onTransfer}>
        <TransferIcon className={classes.actionIcon} /> Transfer
      </button>
    </div>
  )
}

EnrollmentActionButtons.propTypes = {
  displayStatus: PropTypes.string,
  invoice: PropTypes.object,
  paidOnline: PropTypes.bool,
  onDrop: PropTypes.func,
  onRemove: PropTypes.func,
  onTransfer: PropTypes.func,
}

// --- Enrollment Tab ---

const EnrollmentTab = ({ enrollment, displayStatus, onDrop, onRemove, onTransfer }) => {
  const { course, package: pkg, invoice } = enrollment

  const isPending = displayStatus === 'pending_approval'
  const isWaitlisted = displayStatus === 'waitlisted'
  const sectionTitle = (isPending || isWaitlisted) ? 'Details' : 'Enrollment Details'
  const showPackage = !isWaitlisted && !!pkg
  const showStatusBadge = !isPending && !isWaitlisted

  // Merge and sort all activities chronologically (newest first)
  const allActivities = useMemo(() => {
    const merged = [
      ...(enrollment.enrollmentActivities || []).map(a => ({ ...a, source: 'enrollment' })),
      ...(enrollment.invoiceActivities || []).map(a => ({ ...a, source: 'invoice' })),
    ]
    return merged.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  }, [enrollment.enrollmentActivities, enrollment.invoiceActivities])

  const familyAdminEmail = enrollment.student?.familyAdmins?.[0]?.email

  return (
    <div className={classes.tabContent}>
      <EnrollmentActionButtons
        displayStatus={displayStatus}
        invoice={invoice}
        paidOnline={enrollment.paymentStatus === 'paid_online'}
        onDrop={onDrop} onRemove={onRemove}
        onTransfer={onTransfer}
      />

      <section className={classes.detailCard}>
        <div className={classes.detailCardHeader}>
          <h6 className={classes.sectionTitle}>{sectionTitle}</h6>
          {showStatusBadge && (
            <span className={classNames(classes.statusBadge, classes[`status_${displayStatus}`])}>
              {ENROLLMENT_STATUS_LABELS[displayStatus] || displayStatus}
            </span>
          )}
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Program</span>
          <span className={classes.detailValue}>{course?.name}</span>
        </div>

        {showPackage && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Package</span>
            <span className={classes.detailValue}>{pkg.name}</span>
          </div>
        )}

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Facility</span>
          <span className={classes.detailValue}>{course?.communityName || '-'}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Coach</span>
          <span className={classes.detailValue}>{course?.coachName || '-'}</span>
        </div>

        {familyAdminEmail && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Family Admin Email</span>
            <span className={classes.detailValue}>{familyAdminEmail}</span>
          </div>
        )}

        {course?.startDate && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Program Dates</span>
            <span className={classes.detailValue}>
              {moment(course.startDate).format('MM/DD/YYYY')}
              {course.endDate && `-${moment(course.endDate).format('MM/DD/YYYY')}`}
            </span>
          </div>
        )}

        {allActivities.length > 0 && (
          <div className={classes.updatesBlock}>
            <h6 className={classes.sectionTitle}>All Updates</h6>
            <div className={classes.activityTimeline}>
              {allActivities.map(activity => (
                <div key={`${activity.source}-${activity.id}`} className={classes.activityItem}>
                  <div className={classes.activityDot} />
                  <div className={classes.activityContent}>
                    <span className={classes.activityDate}>
                      {moment(activity.createdAt).format('MM/DD/YYYY')}
                    </span>
                    <span className={classes.activityLabel}>
                      {ACTIVITY_LABELS[activity.changeType] || activity.changeType}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

EnrollmentTab.propTypes = {
  enrollment: PropTypes.object.isRequired,
  displayStatus: PropTypes.string,
  onDrop: PropTypes.func,
  onRemove: PropTypes.func,
  onTransfer: PropTypes.func,
}

// --- Billing Tab ---

const BillingTab = ({ enrollment }) => {
  const { invoice, course } = enrollment
  if (!invoice) { return null }

  const invoiceDisplay = formatInvoiceStatusDisplay(invoice)
  const lineItems = invoice.lineItems || []
  const currency = invoice.currencyCode ? { code: invoice.currencyCode } : null
  const familyAdminEmail = enrollment.student?.familyAdmins?.[0]?.email

  return (
    <div className={classes.tabContent}>
      <BillingActionButtons invoice={invoice} />

      <section className={classes.invoiceCard}>
        <div className={classes.invoiceHeader}>
          <h6 className={classes.sectionTitle}>Invoice #{invoice.invoiceNumber}</h6>
          <span className={classNames(classes.invoiceBadge, classes[`invoice_${invoiceDisplay.variant}`])}>
            {invoiceDisplay.label}
          </span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Program</span>
          <span className={classes.detailValue}>{course?.name}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Facility</span>
          <span className={classes.detailValue}>{course?.communityName || '-'}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Coach</span>
          <span className={classes.detailValue}>{course?.coachName || '-'}</span>
        </div>

        {familyAdminEmail && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Family Admin Email</span>
            <span className={classes.detailValue}>{familyAdminEmail}</span>
          </div>
        )}

        {course?.startDate && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Program Dates</span>
            <span className={classes.detailValue}>
              {moment(course.startDate).format('MM/DD/YYYY')}
              {course.endDate && `-${moment(course.endDate).format('MM/DD/YYYY')}`}
            </span>
          </div>
        )}

        {invoice.paymentMethod && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Payment Method</span>
            <span className={classNames(classes.detailValue, classes.paymentMethodValue)}>
              {formatPaymentMethodDisplay(invoice)}
            </span>
          </div>
        )}

        {lineItems.length > 0 && (
          <div className={classes.lineItemsSection}>
            {lineItems.map(item => (
              <div key={item.id} className={classes.lineItemRow}>
                <span className={classes.lineItemLabel}>
                  {item.lineType === 'discount' ? 'Discount' : item.description}
                </span>
                <span className={classes.lineItemAmount}>
                  {currency ? displayWithCurrency(Math.abs(parseFloat(item.amount)), currency) : `$${Math.abs(parseFloat(item.amount)).toFixed(2)}`}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className={classes.totalRow}>
          <span className={classes.totalLabel}>Total</span>
          <span className={classes.totalAmount}>
            {currency ? displayWithCurrency(invoice.total, currency) : `$${parseFloat(invoice.total).toFixed(2)}`}
          </span>
        </div>

        {invoice.notes && (
          <div className={classes.notesSection}>
            <span className={classes.notesTitle}>Additional Notes</span>
            <p className={classes.notesText}>{invoice.notes}</p>
          </div>
        )}
      </section>
    </div>
  )
}

BillingTab.propTypes = {
  enrollment: PropTypes.object.isRequired,
}

// --- Billing Action Buttons ---

const BillingActionButtons = ({ invoice }) => {
  if (!invoice) { return null }

  const isOutstanding = invoice.status === 'outstanding'

  if (isOutstanding) {
    return (
      <div className={classes.actionButtons}>
        <button className={classes.actionButton} disabled>
          Send Reminder
        </button>
        <button className={classes.actionButton} disabled>
          Mark as Paid
        </button>
        <button className={classes.actionButton} disabled>
          Edit
        </button>
      </div>
    )
  }

  return (
    <div className={classes.actionButtons}>
      <button className={classes.actionButton} disabled>
        <RefundIcon className={classes.actionIcon} /> Drop/Refund
      </button>
      <button className={classes.actionButton} disabled>
        Mark as Outstanding
      </button>
    </div>
  )
}

BillingActionButtons.propTypes = {
  invoice: PropTypes.object,
}

// --- Contact Tab ---

const ContactTab = ({ enrollment }) => {
  const navigate = useNavigate()
  const { student, communityEnrollments } = enrollment
  const familyAdmin = student.familyAdmins?.[0]
  const hasGender = familyAdmin?.gender !== null && familyAdmin?.gender !== undefined

  const handleCopy = (e, text) => {
    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard.writeText(text)
    toast.success('Copied email to clipboard!', { position: toast.POSITION.TOP_RIGHT })
  }

  return (
    <div className={classes.tabContent}>
      <section className={classes.quickActions}>
        <h6 className={classes.sectionTitle}>Quick Actions & Administration</h6>
        <button
          className={classes.quickButton}
          onClick={() => navigate(`/profile/${student.id}`)}
        >
          <PersonIcon width={22} height={22} color={colors.secondaryColor} />
          View Profile
        </button>
        {student.familyId && (
          <button
            className={classes.quickButton}
            onClick={() => navigate(`/members/families/${student.familyId}`)}
          >
            <FamilyIcon width={22} height={22} color={colors.secondaryColor} />
            View Family Page
          </button>
        )}
      </section>

      {familyAdmin && (
        <section className={classes.contactSection}>
          <h6 className={classes.sectionTitle}>Contact Family Admin</h6>
          <div className={classes.adminProfile}>
            <ProfileAvatar student={familyAdmin} avatarClassName='small-round-image-wrapper' />
            <div className={classes.adminInfo}>
              <div className={classes.adminName}>
                <span className='font-weight-bold'>{familyAdmin.firstName} {familyAdmin.lastName}</span>
                <span className={classes.familyAdminLabel}>Family Admin</span>
              </div>
              <span className={classes.adminMeta}>
                {familyAdmin.birthdate && `Age ${calculateAge(familyAdmin.birthdate)}`}
                {familyAdmin.birthdate && hasGender && ' / '}
                {hasGender && genderLabel[familyAdmin.gender]?.charAt(0)}
              </span>
            </div>
          </div>

          {familyAdmin.phonePrefix && familyAdmin.phone && (
            <a
              className={classes.contactRow}
              href={`tel:${formatPhoneForLinks(familyAdmin.phonePrefix, familyAdmin.phone)}`}
            >
              <CallIcon className={classes.contactIcon} />
              <span>{familyAdmin.phonePrefix} {familyAdmin.phone}</span>
            </a>
          )}

          {familyAdmin.email && (
            <a
              className={classes.contactRow}
              href={`mailto:${familyAdmin.email}`}
            >
              <MailIcon className={classes.contactIcon} />
              <div className={classes.emailWrapper}>
                <button
                  onClick={(e) => handleCopy(e, familyAdmin.email)}
                  className={classes.copyButton}
                  aria-label='Copy email address'
                >
                  <CopyIcon className={classes.copyIcon} />
                </button>
                {familyAdmin.email}
              </div>
            </a>
          )}
        </section>
      )}

      {communityEnrollments?.length > 0 && (
        <section className={classes.programsSection}>
          <h6 className={classes.sectionTitle}>Programs Currently Enrolled in</h6>
          {communityEnrollments.map(e => (
            <div key={e.id} className={classes.programCard}>
              <span className={classes.programName}>{e.courseName}</span>
              {e.courseStartDate && (
                <span className={classes.programMetaRow}>
                  <CalendarIcon className={classes.programCalendarIcon} />
                  <span>
                    {moment(e.courseStartDate).format('MMM D, YYYY')}
                    {e.courseEndDate && ` - ${moment(e.courseEndDate).format('MMM D, YYYY')}`}
                  </span>
                </span>
              )}
              {(e.golfersCount > 0 || e.courseMeetingTimes) && (
                <span className={classes.programMetaRow}>
                  {e.golfersCount > 0 && (
                    <>
                      <GolferIcon className={classes.programGolferIcon} />
                      <span className={classes.programGolferCount}>{e.golfersCount}</span>
                    </>
                  )}
                  {e.courseMeetingTimes && <span>{e.courseMeetingTimes}</span>}
                </span>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

ContactTab.propTypes = {
  enrollment: PropTypes.object.isRequired,
}

// --- Main component PropTypes ---

EnrollmentDetailDrawer.propTypes = {
  enrollment: PropTypes.object.isRequired,
  communityId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onActionComplete: PropTypes.func,
}

export default EnrollmentDetailDrawer
